import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { dataImportAPI } from '../../services/api';

const DataImport: React.FC = () => {
  const { data: importJobs, isLoading } = useQuery({
    queryKey: ['importJobs'],
    queryFn: dataImportAPI.getImportJobs
  });

  const jobs = importJobs?.data?.results || [];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'processing':
        return <ScheduleIcon color="warning" />;
      default:
        return <ScheduleIcon color="action" />;
    }
  };

  const getStatusChip = (status: string) => {
    const colorMap: any = {
      completed: 'success',
      failed: 'error',
      processing: 'warning',
      pending: 'default',
      cancelled: 'default',
    };

    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={colorMap[status] || 'default'}
        size="small"
      />
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Data Import
      </Typography>

      <Grid container spacing={3}>
        {/* Upload Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Upload New Data
            </Typography>
            
            <Card
              sx={{
                border: '2px dashed #ccc',
                borderColor: 'primary.light',
                backgroundColor: 'grey.50',
                textAlign: 'center',
                p: 4,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'primary.50',
                },
              }}
            >
              <CardContent>
                <UploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Drop files here or click to upload
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Supported formats: CSV, Excel (.xlsx, .xls)
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  sx={{ mt: 2 }}
                >
                  Choose Files
                  <input type="file" hidden accept=".csv,.xlsx,.xls" />
                </Button>
              </CardContent>
            </Card>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Supported data types:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="School Information"
                    secondary="Basic school details and contact information"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Infrastructure Data"
                    secondary="Buildings, classrooms, and facilities"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Enrollment Data"
                    secondary="Student enrollment by grade and gender"
                  />
                </ListItem>
              </List>
            </Box>
          </Paper>
        </Grid>

        {/* Recent Imports */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Recent Imports
            </Typography>
            
            <Box sx={{ height: 300, overflow: 'auto' }}>
              <List>
                {isLoading ? (
                  <ListItem>
                    <ListItemText primary="Loading..." />
                  </ListItem>
                ) : jobs.length === 0 ? (
                  <ListItem>
                    <ListItemText
                      primary="No imports yet"
                      secondary="Upload your first data file to get started"
                    />
                  </ListItem>
                ) : (
                  jobs.map((job: any) => (
                    <ListItem key={job.id} divider>
                      <ListItemIcon>
                        {getStatusIcon(job.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">
                              {job.name}
                            </Typography>
                            {getStatusChip(job.status)}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              {job.source_type.toUpperCase()} • {new Date(job.created_at).toLocaleDateString()}
                            </Typography>
                            {job.status === 'processing' && job.progress_percentage > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={job.progress_percentage}
                                  sx={{ height: 4, borderRadius: 2 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {Math.round(job.progress_percentage)}% complete
                                </Typography>
                              </Box>
                            )}
                            {job.status === 'completed' && (
                              <Typography variant="caption" color="success.main">
                                {job.successful_records} records imported successfully
                              </Typography>
                            )}
                            {job.status === 'failed' && (
                              <Typography variant="caption" color="error.main">
                                Import failed - check error logs
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </Box>
          </Paper>
        </Grid>

        {/* Import Statistics */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Import Statistics
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main">
                      {jobs.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Imports
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {jobs.filter((j: any) => j.status === 'completed').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Successful
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      {jobs.filter((j: any) => j.status === 'processing').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      In Progress
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="error.main">
                      {jobs.filter((j: any) => j.status === 'failed').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Failed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DataImport;